<template>
    <ExperienceTypeSelect :class="$style.types" @select="handleSelectType"></ExperienceTypeSelect>
    <TemplateRecommandList
        :class="$style.recommandList"
        size="large"
        :showGalleryCard="true"
        position="select_type_recommend"
        location="Create Experience popup"
        :entryPosition="props.entryPosition"
        @view="handleViewTemplate"
        @create="handleCreateWithTemplate"
    >
        <template #left>
            <p :class="$style.title">
                {{ $t('experience.onboarding.recommand_template_title') }}
            </p>
        </template>
    </TemplateRecommandList>
    <Teleport v-if="detailDialogVisible" to="body">
        <ExperienceTemplateDetailDialog
            position="select_type_recommend"
            :entryPosition="props.entryPosition"
            :visible="detailDialogVisible"
            :template="currentSelectTemplate"
            @view="handleCreateWithTemplate"
            @create="handleCreateWithTemplate"
            @close="detailDialogVisible = false"
        ></ExperienceTemplateDetailDialog>
    </Teleport>
</template>

<script lang="ts">
import TemplateRecommandList from '@experience/components/template/TemplateRecommandList.vue';
import ExperienceTypeSelect from '@experience/components/template/ExperienceTypeSelect.vue';
import ExperienceTemplateDetailDialog from '@experience/components/dialog/ExperienceTemplateDetailDialog.vue';

export default {
    name: 'ExperienceCreateEntry',
    components: {
        TemplateRecommandList,
        ExperienceTypeSelect,
        ExperienceTemplateDetailDialog
    }
};
</script>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import analyticsService from '@assets/services/analytics.service';
import {
    useCreateDataStore,
    ComponentData,
    EntryPosition,
    CreateData,
    DialogData
} from '@experience/store/experience.create.store';
import type { ECTemplate } from '@models/ecshop/asset';
import type { TypeItem } from '@experience/static/config/experience.type';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';

interface Props {
    entryItem: TypeItem;
    entryPosition: EntryPosition;
    data: ComponentData;
}
const props = defineProps<Props>();
const emit = defineEmits(['close', 'update', 'createWithTemplate']);
const detailDialogVisible = ref(false);
const currentSelectTemplate = ref();
const { t } = useI18n();
const { updateExperienceCreateData, createExperienceHandler } = useCreateDataStore();

async function handleSelectType(item: TypeItem) {
    const { key, trackKey } = item;
    const data: CreateData = {
        entryItem: item,
        experienceType: key as any
    };
    data.routePath = key;
    const dialogData: DialogData = {
        component: '',
        backComponent: 'ExperienceCreateEntry'
    };

    if (['ADVANCED', 'REDIRECT'].includes(key)) {
        data.dialog = dialogData;
        dialogData.component = key !== 'REDIRECT' ? 'ExperienceForm' : '';
        dialogData.componentData = {
            visibleBack: false
        };
    } else if (key === 'ABTEST') {
        dialogData.componentData = {
            visibleBack: true
        };
        dialogData.component = 'ExperienceABTestTypeSelect';
        data.fullscreenDialog = dialogData;
    } else {
        dialogData.component = 'ExperienceTemplate';
        data.fullscreenDialog = dialogData;
    }

    analyticsService
        .usePTX('cp_add_select_type', {
            type: trackKey
        })
        .track();
    // 如果是redirect 则直接创建进setting页
    if (key === 'REDIRECT') {
        updateExperienceCreateData(data);
        await createExperienceHandler();
    } else {
        emit('update', data);
    }
}

function handleClose() {
    emit('close');
}
function handleViewTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    detailDialogVisible.value = true;
    currentSelectTemplate.value = template;
}

function handleCreateWithTemplate(
    template: ExperienceTemplateV4 | ECTemplate,
    templateSource: 'COMMON' | 'OWN' = 'COMMON'
) {
    emit('createWithTemplate', {
        template,
        templateSource,
        backTemplateName: ''
    });
    detailDialogVisible.value = false;
}
</script>

<style lang="scss" module>
@import '@style/import.scss';

.types {
    margin-top: 8px;
}
.recommandList {
    margin-top: 100px;
    .title {
        font-size: 16px;
        color: $pt-black-900;
        margin-bottom: 20px;
    }
}
</style>
